import { Add, ChevronRight, Edit, ExpandLess } from '@mui/icons-material';
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  deleteFinancialStatementNode,
  getFinancialStatementsTreeNodesArray,
} from '../../../../../../lib/api/finnacialStatements';
import CustomButton from '../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import EditNodeDialog from './components/EditNodeDialog';

interface TreeNode {
  id: string;
  key: string;
  children?: TreeNode[];
  accountAuxiliaryCode?: string | null;
  accountCode?: string | null;
  accountCodeType?: string | null;
  businessIdentifierRequired?: boolean;
  classId?: string | null;
  classifiedCategoryId?: string | null;
  costCenterRequired?: boolean;
  documentKeyRequired?: boolean;
  father?: number;
  financialStatementsTreeTypeId?: number;
  internalKey?: string | null;
}

interface TreeItemProps {
  node: TreeNode;
  level: number;
  onNodeUpdate: (nodeId: string, updatedNode: TreeNode) => void;
  onNodeDelete: (nodeId: string) => void;
  allNodes: TreeNode[]; // Add allNodes prop
}

const TreeItem: React.FC<TreeItemProps> = ({
  node,
  level,
  onNodeUpdate,
  onNodeDelete,
  allNodes,
}) => {
  const [open, setOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const hasChildren = node.children && node.children.length > 0;

  const handleToggle = () => {
    if (hasChildren) {
      setOpen(!open);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
  };

  return (
    <>
      <ListItem
        button
        onClick={handleToggle}
        sx={{
          pl: level * 2 + 2,
          py: 0.5,
          backgroundColor: node.internalKey
            ? 'rgba(0, 0, 0, 0.02)'
            : 'transparent',
          '&:hover': {
            backgroundColor: node.internalKey
              ? 'rgba(0, 0, 0, 0.06)'
              : 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          {hasChildren ? (
            open ? (
              <ExpandLess color="primary" />
            ) : (
              <ChevronRight color="primary" />
            )
          ) : (
            <ChevronRight sx={{ opacity: 0.3 }} />
          )}
        </ListItemIcon>
        <ListItemText
          primary={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: hasChildren ? 'bold' : 'normal',
                  color: node.internalKey ? 'text.secondary' : 'text.primary',
                  fontStyle: node.internalKey ? 'italic' : 'normal',
                }}
              >
                {node.key} {node.accountCode ? `| ${node.accountCode}` : ''}
                {node.internalKey && (
                  <span
                    style={{
                      fontSize: '0.8em',
                      marginLeft: '4px',
                      opacity: 0.7,
                    }}
                  >
                    (Sistema)
                  </span>
                )}{' '}
              </Typography>
              <IconButton size="small" onClick={handleEditClick} sx={{ ml: 1 }}>
                <Edit fontSize="small" />
              </IconButton>
            </div>
          }
        />
      </ListItem>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {node.children?.map((child) => (
              <TreeItem
                key={child.id}
                node={child}
                level={level + 1}
                onNodeUpdate={onNodeUpdate}
                onNodeDelete={onNodeDelete}
                allNodes={allNodes}
              />
            ))}
          </List>
        </Collapse>
      )}

      <EditNodeDialog
        open={editDialogOpen}
        node={node}
        isNewNode={false}
        allNodes={allNodes}
        onClose={handleCloseDialog}
        onSave={onNodeUpdate}
        onDelete={onNodeDelete}
      />
    </>
  );
};

const FinancialNodes = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [treeTypes, setTreeTypes] = useState<any[]>([]);

  const fetchTreeData = async () => {
    console.log('fetchTreeData called');
    setLoading(true);
    try {
      const response = await getFinancialStatementsTreeNodesArray(dispatch);
      console.log('fetchTreeData response:', response);
      setTreeData(response || []);
      console.log('Tree data updated with', response?.length || 0, 'nodes');
      //TODO: Get tree types from API
      setTreeTypes([
        { id: 1, name: 'Activos' },
        { id: 2, name: 'Pasivos' },
        { id: 3, name: 'Estado de resultados' },
      ]);
    } catch (error) {
      console.error('Error fetching tree data:', error);
    }
    setLoading(false);
  };

  const updateNodeInTree = (
    nodes: TreeNode[],
    nodeId: string,
    updatedNode: TreeNode,
  ): TreeNode[] => {
    return nodes.map((node) => {
      if (node.id === nodeId) {
        return updatedNode;
      }
      if (node.children) {
        return {
          ...node,
          children: updateNodeInTree(node.children, nodeId, updatedNode),
        };
      }
      return node;
    });
  };

  const handleNodeUpdate = async (nodeId: string, updatedNode: TreeNode) => {
    setTreeData((prevData) => updateNodeInTree(prevData, nodeId, updatedNode));
    await fetchTreeData();
  };

  const handleNodeDelete = async (nodeId: string) => {
    try {
      await deleteFinancialStatementNode(dispatch, nodeId);
      await fetchTreeData();
    } catch (error) {
      await fetchTreeData();
    }
  };

  const getAllNodes = () => {
    const allNodes: TreeNode[] = [];

    const addNodes = (nodes: TreeNode[]) => {
      nodes.forEach((node) => {
        allNodes.push(node);
        if (node.children) {
          addNodes(node.children);
        }
      });
    };

    addNodes(treeData);
    return allNodes;
  };

  useEffect(() => {
    fetchTreeData();
  }, []);

  if (loading) {
    return <CenteredCircularProgress type="layout" />;
  }

  return (
    <div style={{ padding: '20px' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h5" gutterBottom>
          Plan de Cuentas
        </Typography>
        <CustomButton
          color="primary"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Agregar Cuenta
        </CustomButton>
      </Box>
      <Paper
        elevation={1}
        sx={{
          p: 2,
          minHeight: '400px',
          maxHeight: '600px',
          overflow: 'auto',
        }}
      >
        <List component="nav" dense>
          {treeData.map((node) => (
            <TreeItem
              key={node.id}
              node={node}
              level={0}
              onNodeUpdate={handleNodeUpdate}
              onNodeDelete={handleNodeDelete}
              allNodes={getAllNodes()}
            />
          ))}
        </List>
      </Paper>

      <EditNodeDialog
        open={createDialogOpen}
        node={null}
        isNewNode={true}
        allNodes={getAllNodes()}
        onClose={() => setCreateDialogOpen(false)}
        onSave={async () => await fetchTreeData()}
        onSuccess={async () => await fetchTreeData()}
        onError={(error: any) => {
          console.error('Error:', error);
          fetchTreeData();
        }}
        setLoading={(loading: boolean) => console.log('Loading:', loading)}
        fetchSetupData={async () => await fetchTreeData()}
        onDelete={handleNodeDelete}
      />
    </div>
  );
};

export default FinancialNodes;
